import { useMutation, useQuery, UseQueryOptions } from "react-query";
import HttpService from "../../services/http";
import { AxiosError } from "axios";
import { toast } from "react-toastify";
import { queryKeys } from "../../constants/queryKeys";
import { queryClient } from "../../providers/QueryProvider";
import useAuth from "../useAuth";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLmcUserView } from "../../contexts/AuthContext";
import { AuthActionKind } from "../../contexts/AuthContext";
import SecureStorage from "../../utils/secureStorage";
import { UniversalCacheManager } from "../../utils/cacheManager";
import { entityCache } from "../entity";
import { groupsCache } from "../groups";
import i18next from "i18next";
import { notDeletedFiles, toastNotDeletedFiles } from "../cdn";

export const usersCache = new UniversalCacheManager();

type UpdateTokenProp = {
  customer_identification: string;
  entityIdentification: string;
};
type SearchUsersProp = {
  customer_identification: string;
  entityIdentification: string;
  pageParam: number;
  limit: number;
  query?: string;
};

type DeleteOwnerProp = {
  entity_identification: string;
  user_identification: string;
  customer_identification: string;
  validation: string;
};

type GetRoleProps = {
  user_identification: string;
  entity_identification: string;
  enabled?: boolean;
};

type UpdateRoleProp = {
  user_identification: string;
  entity_identification: string;
  customerId: string;
  role: string;
};

type UpdateTypeProp = {
  user_identification: string;
  entity_identification: string;
  customerId: string;
  type: string;
};

type UpdateTracker = {
  customer_identification: string;
  entity_identification: string;
  user_identification: string;
  events: string[];
  from?: string;
  to?: string;
};

type GetEntityUSerProps = {
  customer_identification: string;
  entityIdentification: string;
  user_identification: string;
};

type ValidateStatus = {
  customer_identification: string;
  entity_identification: string;
  userId: string;
  status: string;
};

type UpdateUserProps = {
  customer_identification: string;
  entity_identification: string;
  user_identification: string;
  data: any;
  authorization?: any;
};

type UserDataWithSpecialKey = {
  customer_identification: string;
  entity_identification: string;
  customerId: string;
  key: string;
  data?: any;
  new_key?: string;
};

interface ConnectionInfoData {
  timezone?: string;
  language?: string;
  platform?: string;
  referrer?: string;
  browserName?: string;
  browserVersion?: string;
  isMobile?: boolean;
}

type SaveStatisticsBusinessCard = {
  entity_identification: string;
  user_identification?: string;
  event_key: string;
  data: ConnectionInfoData;
};

export const useGetSearchUsers = ({
  customer_identification,
  entityIdentification,
  pageParam,
  limit,
  query,
}: SearchUsersProp) => {
  const offset = pageParam * limit;
  return useQuery<any, any, any>(
    [
      queryKeys.searchUsers,
      customer_identification,
      entityIdentification,
      query,
      pageParam,
      limit,
    ],
    async () => {
      const params = new URLSearchParams();
      if (query) params.append("query", query);
      params.append("limit", limit.toString());
      params.append("offset", offset.toString());

      return await HttpService.get(
        `/${customer_identification}/${entityIdentification}/user/search?${params.toString()}`
      ).then((data) => {
        if (data?.data?.code === "TOO_MANY_RESULTS") {
          return { tooManyResults: true };
        }
        return data?.data?.element;
      });
    },
    {
      enabled: !!query && query.trim().length >= 5,
      onError: (err: AxiosError<any, any>) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useDeleteOwner = () => {
  const auth = useAuth();
  const navigate = useNavigate();

  return useMutation<DeleteOwnerProp, AxiosError<any, any>, any>(
    ({
      user_identification,
      entity_identification,
      customer_identification,
      validation,
    }: DeleteOwnerProp) =>
      HttpService.delete(
        `/${customer_identification}/${entity_identification}/user/owner/${user_identification}`,
        {
          data: {
            validation: validation,
          },
        }
      ),
    {
      onSuccess: () => {
        toast.success("User account deleted successfully");
        SecureStorage.clearAuthData();
        SecureStorage.clearUserPreferences();

        auth.logout();

        queryClient.clear();

        navigate("/");
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useUpdateToken = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({ entityIdentification, customer_identification }: UpdateTokenProp) =>
      HttpService.patch(
        `/${customer_identification}/${entityIdentification}/user/${customer_identification}/access-token`,
        null
      ),
    {
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useGetRoleList = ({
  user_identification,
  entity_identification,
  enabled = true,
}: GetRoleProps) => {
  return useQuery<any, any, any>(
    ["roles_list", user_identification],
    async () => {
      const cacheKey = `entity_roles_${user_identification}`;
      const cached = usersCache.get(cacheKey);

      if (cached) {
        return cached;
      }

      const data = await HttpService.get(
        `/${user_identification}/${entity_identification}/entity/roles`
      ).then((response) => {
        return response?.data?.element;
      });

      usersCache.setWithLongTTL(cacheKey, data);
      return data;
    },
    {
      enabled,
      onError: (err: AxiosError<any, any>) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useGetTypeList = ({
  user_identification,
  entity_identification,
  enabled = true,
}: GetRoleProps) => {
  return useQuery<any, any, any>(
    ["types_list", user_identification],
    async () => {
      const cacheKey = `entity_types_${user_identification}`;
      const cached = usersCache.get(cacheKey);

      if (cached) {
        return cached;
      }

      const data = await HttpService.get(
        `/${user_identification}/${entity_identification}/entity/types`
      ).then((response) => {
        return response?.data?.element;
      });

      usersCache.setWithMediumTTL(cacheKey, data);
      return data;
    },
    {
      enabled,
      onError: (err: AxiosError<any, any>) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useUpdateRole = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      user_identification,
      entity_identification,
      customerId,
      role,
    }: UpdateRoleProp) =>
      HttpService.patch(
        `/${user_identification}/${entity_identification}/user/${customerId}/role/${role}`,
        null
      ),
    {
      onSuccess: (res, variables) => {
        usersCache.clearByPattern("entity_roles_");

        const user = `entity_users`;
        usersCache.clearByPattern(user);

        queryClient.invalidateQueries(["roles_list"], {
          exact: false,
          refetchActive: true,
        });

        queryClient.invalidateQueries(["entity-users"], {
          exact: false,
          refetchActive: true,
        });
        queryClient.invalidateQueries(["searchUsers"]);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useUpdateType = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      user_identification,
      entity_identification,
      customerId,
      type,
    }: UpdateTypeProp) =>
      HttpService.patch(
        `/${user_identification}/${entity_identification}/user/${customerId}/type/${type}`,
        null
      ),
    {
      onSuccess: (res, variables) => {
        usersCache.clearByPattern("entity_types_");

        const user = `entity_users`;
        usersCache.clearByPattern(user);

        queryClient.invalidateQueries(["types_list"], {
          exact: false,
          refetchActive: true,
        });

        queryClient.invalidateQueries(["entity-users"], {
          exact: false,
          refetchActive: true,
        });
        queryClient.invalidateQueries(["searchUsers"]);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useGetTrackerOptions = ({
  customer_identification,
  entityIdentification,
}: UpdateTokenProp) => {
  return useQuery<any, any, any>(
    [queryKeys.trackerOptions, customer_identification, entityIdentification],
    async () => {
      return await HttpService.get(
        `/${customer_identification}/${entityIdentification}/user/events`
      ).then((data) => {
        return data?.data?.element;
      });
    },
    {
      onError: (err: AxiosError<any, any>) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useUpdateTracker = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
      user_identification,
      events,
      from,
      to,
    }: UpdateTracker) =>
      HttpService.get(
        `/${customer_identification}/${entity_identification}/user/${user_identification}/events?events=${events}&limit=100${
          from !== "" && from !== undefined ? `&from=${from}` : ""
        }${to !== "" && to !== undefined ? `&to=${to}` : ""}`
      ),
    {
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useGetEntityUser = ({
  customer_identification,
  entityIdentification,
  user_identification,
}: GetEntityUSerProps) => {
  return useQuery<any, any, any>(
    ["entity_user", user_identification],
    async () => {
      const cacheKey = `user_profile_${user_identification}`;
      const cached = usersCache.get(cacheKey);

      if (cached) {
        return cached;
      }

      const data = await HttpService.get(
        `/${customer_identification}/${entityIdentification}/user/${user_identification}/profile`
      ).then((response) => {
        return response?.data?.element;
      });
      usersCache.setWithMediumTTL(cacheKey, data);
      return data;
    }
  );
};

export const useGetUserPreferences = (
  customer_identification: string,
  entityIdentification: string,
  user_identification: string
) => {
  return useQuery<any, any, any>(
    ["user-preferences", user_identification],
    async () => {
      const cacheKey = `user_preferences_all_${user_identification}`;
      const cached = usersCache.get(cacheKey);

      if (cached) {
        return cached;
      }

      const data = await HttpService.get(
        `/${customer_identification}/${entityIdentification}/user/${user_identification}/preferences`
      ).then((response) => {
        return response?.data?.element;
      });

      usersCache.setWithLongTTL(cacheKey, data);
      return data;
    },
    {
      onError: (err: AxiosError<any, any>) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useGetUserPreferencesWithSpecialKey = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
      customerId,
      key,
    }: UserDataWithSpecialKey) =>
      HttpService.get(
        `/${customer_identification}/${entity_identification}/user/${customerId}/preferences/${key}`
      ),
    {
      onSuccess: () => {},
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useUpdateUserPreferencesWithSpecialKey = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
      customerId,
      key,
      data,
    }: UserDataWithSpecialKey) =>
      HttpService.patch(
        `/${customer_identification}/${entity_identification}/user/${customerId}/preferences/${key}`,
        { user_data_preferences: data }
      ),
    {
      onSuccess: (data, variables) => {
        const preferencesCacheKey = `user_preferences_all_${variables.customerId}`;
        usersCache.delete(preferencesCacheKey);
        queryClient.invalidateQueries([
          "user-preferences",
          variables.customerId,
        ]);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useCreateUserPreferencesWithSpecialKey = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
      customerId,
      key,
      data,
    }: UserDataWithSpecialKey) =>
      HttpService.post(
        `/${customer_identification}/${entity_identification}/user/${customerId}/preferences/${key}`,
        { user_data_preferences: data }
      ),
    {
      onSuccess: (data, variables) => {
        const preferencesCacheKey = `user_preferences_all_${variables.customerId}`;
        usersCache.delete(preferencesCacheKey);

        queryClient.invalidateQueries([
          "user-preferences",
          variables.customerId,
        ]);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useDeleteUserFilesAndDocuments = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
      customerId,
      folder,
      files,
    }: {
      customer_identification: string;
      entity_identification: string;
      customerId: string;
      folder: string;
      files: Array<{ file_name: string; ai_engine_doc_id?: string }>;
    }) =>
      HttpService.delete(
        `/${customer_identification}/${entity_identification}/user/${customerId}/preferences/${folder}/files`,
        { data: { files } }
      ),
    {
      onSuccess: (response) => {
        const notDeleted = notDeletedFiles(response);
        if (notDeleted.length > 0) toastNotDeletedFiles(notDeleted);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useDeleteUserPreferenceField = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
      customerId,
      folder,
      field_key,
    }: {
      customer_identification: string;
      entity_identification: string;
      customerId: string;
      folder: string;
      field_key: string;
    }) =>
      HttpService.delete(
        `/${customer_identification}/${entity_identification}/user/${customerId}/preferences/${folder}/fields/${field_key}`
      ),
    {
      onSuccess: (data, variables) => {
        const preferencesCacheKey = `user_preferences_all_${variables.customerId}`;
        usersCache.delete(preferencesCacheKey);

        queryClient.invalidateQueries([
          "user-preferences",
          variables.customerId,
        ]);
      },
      onError: (err: AxiosError<any, any>) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useDeleteUserPreferencesWithSpecialKey = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
      customerId,
      key,
      data,
    }: UserDataWithSpecialKey) =>
      HttpService.delete(
        `/${customer_identification}/${entity_identification}/user/${customerId}/preferences/${key}`
      ),
    {
      onSuccess: (data, variables) => {
        const preferencesCacheKey = `user_preferences_all_${variables.customerId}`;
        usersCache.delete(preferencesCacheKey);

        queryClient.invalidateQueries([
          "user-preferences",
          variables.customerId,
        ]);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useRenameUserPreferencesWithSpecialKey = () => {
  return useMutation<
    {
      customer_identification: string;
      entity_identification: string;
      customerId: string;
      key: string;
      new_key: string;
      data: string;
    },
    AxiosError<any, any>,
    any
  >(
    ({
      customer_identification,
      entity_identification,
      customerId,
      key,
      new_key,
      data,
    }: UserDataWithSpecialKey) =>
      HttpService.patch(
        `/${customer_identification}/${entity_identification}/user/${customerId}/preferences/${key}/rename`,
        {
          user_data_preferences: data,
          new_key: new_key,
        }
      ),
    {
      onSuccess: (data, variables) => {
        const preferencesCacheKey = `user_preferences_all_${variables.customerId}`;
        usersCache.delete(preferencesCacheKey);

        queryClient.invalidateQueries([
          "user-preferences",
          variables.customerId,
        ]);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useDeleteEntityUser = () => {
  return useMutation<DeleteOwnerProp, AxiosError<any, any>, any>(
    ({
      user_identification,
      entity_identification,
      customer_identification,
    }: DeleteOwnerProp) =>
      HttpService.delete(
        `/${customer_identification}/${entity_identification}/user/${user_identification}`
      ),
    {
      onSuccess: (res: any, variables) => {
        const leftovers = res?.data?.element?.files_not_deleted;
        const leftoverCount = Array.isArray(leftovers) ? leftovers.length : 0;
        if (leftoverCount > 0) {
          toast.warning(
            i18next.t("userDeletion.removedWithLeftovers", {
              count: leftoverCount,
            }) as string
          );
        } else {
          toast.success(i18next.t("userDeletion.removed") as string);
        }

        const countCacheKey = `entity_count_users_${variables.entity_identification}`;
        usersCache.delete(countCacheKey);

        const statsCacheKeyPattern = `entity_user_statistics_${variables.entity_identification}`;
        const entityUsersPattern = `entity_users_${variables.entity_identification}`;
        usersCache.clearByPattern(statsCacheKeyPattern);
        usersCache.clearByPattern(entityUsersPattern);

        const user = `entity_users`;
        usersCache.clearByPattern(user);

        queryClient.invalidateQueries(["count-users"]);
        queryClient.invalidateQueries(["entity-users"], {
          exact: false,
          refetchActive: true,
        });
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useResendValidationLink = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
      user_identification,
    }: UpdateTracker) =>
      HttpService.post(
        `/${customer_identification}/${entity_identification}/user/${user_identification}/validation-link`,
        null
      ),
    {
      onSuccess: () => {
        toast.success("Validation link is sent.");
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useGenratePassword = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
      user_identification,
    }: UpdateTracker) =>
      HttpService.post(
        `/${customer_identification}/${entity_identification}/user/${user_identification}/password/generate-password`,
        null
      ),
    {
      onSuccess: () => {
        toast.success("Password is generated.");
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useValidateStatus = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
      userId,
      status,
    }: ValidateStatus) =>
      HttpService.patch(
        `/${customer_identification}/${entity_identification}/user/${userId}/account/status`,
        { status }
      ),
    {
      onSuccess: (res, variables) => {
        const profileCacheKey = `user_profile_${variables.userId}`;
        usersCache.delete(profileCacheKey);

        queryClient.invalidateQueries(["entity_user", variables.userId]);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useUpdateUserProfile = () => {
  const { t } = useTranslation();
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
      user_identification,
      data,
    }: UpdateUserProps) =>
      HttpService.patch(
        `/${customer_identification}/${entity_identification}/user/${user_identification}/profile`,
        data
      ),
    {
      onSuccess: (res, variables) => {
        const profileCacheKey = `user_profile_${variables.user_identification}`;
        usersCache.delete(profileCacheKey);

        queryClient.invalidateQueries("profile");
        queryClient.invalidateQueries([
          "entity_user",
          variables.user_identification,
        ]);
        toast.success(t("dashboard.userProfile.profileForm.updateSuccess"));
      },
      onError: (err) => {
        for (const [, value] of Object.entries(err.response?.data.element)) {
          if (typeof value === "string") toast.error(value);
        }
      },
    }
  );
};

export const useAdminUpdateUserEmail = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
      user_identification,
      data,
    }: UpdateUserProps) =>
      HttpService.patch(
        `/${customer_identification}/${entity_identification}/user/${user_identification}/email`,
        data
      ),
    {
      onSuccess: (res, variables) => {
        queryClient.invalidateQueries([
          "entity_user",
          variables.user_identification,
        ]);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useUpdateUserProfileAvatar = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
      user_identification,
      data,
    }: UpdateUserProps) =>
      HttpService.post(
        `/${customer_identification}/${entity_identification}/user/${user_identification}/profile/avatar`,
        data
      ),
    {
      onSuccess: (res, variables) => {
        const profileCacheKey = `user_profile_${variables.user_identification}`;
        usersCache.delete(profileCacheKey);

        queryClient.invalidateQueries(["profile"]);
        queryClient.invalidateQueries([
          "entity_user",
          variables.user_identification,
        ]);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useDeleteUserProfileAvatar = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
      user_identification,
    }: UpdateUserProps) =>
      HttpService.delete(
        `/${customer_identification}/${entity_identification}/user/${user_identification}/profile/avatar`
      ),
    {
      onSuccess: (res, variables) => {
        const cacheKey = `user_profile_${variables.user_identification}`;
        usersCache.delete(cacheKey);

        queryClient.invalidateQueries(["profile"]);
        queryClient.invalidateQueries([
          "entity_user",
          variables.user_identification,
        ]);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useResetPassword = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      user_identification,
      token,
      data,
    }: {
      user_identification: string;
      token: string;
      data: any;
    }) =>
      HttpService.patch(
        `/auth/${user_identification}/${token}/change-password`,
        data
      ),
    {
      onSuccess: (data) => {},
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useUserResetPassword = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      entityId,
      userId,
      token,
      data,
    }: {
      entityId: string;
      userId: string;
      token: string;
      data: any;
    }) =>
      HttpService.patch(`/user/${entityId}/${userId}/${token}/password`, data),
    {
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useGetCountUsers = (
  customer_identification: string,
  entityIdentification: string,
  options?: Omit<
    UseQueryOptions<any, AxiosError<any, any>, any>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery<any, any, any>(
    ["count-users"],
    async () => {
      const cacheKey = `entity_count_users_${entityIdentification}`;
      const cached = usersCache.get(cacheKey);

      if (cached) {
        return cached;
      }

      const data = await HttpService.get(
        `/${customer_identification}/${entityIdentification}/entity/count-users`
      ).then((response) => {
        return response.data;
      });

      usersCache.setWithMediumTTL(cacheKey, data);
      return data;
    },
    options
  );
};

export const useGetEntityUsers = (
  customer_identification: string,
  entityIdentification: string,
  limit: number,
  offset: number,
  sortBy: string,
  sortOrder: string,
  options?: UseQueryOptions<
    any,
    AxiosError<any, any>,
    any,
    [string, string, string, number, number, string, string]
  >
) => {
  return useQuery(
    [
      "entity-users",
      customer_identification,
      entityIdentification,
      limit,
      offset,
      sortBy,
      sortOrder,
    ],
    async () => {
      const validSortOrder =
        sortOrder === "ASC" || sortOrder === "DESC" ? sortOrder : "DESC";
      const validSortBy =
        sortBy && sortBy !== "undefined" && sortBy !== "null"
          ? sortBy
          : "CREATION_DATE";

      const cacheKey = `entity_users_${entityIdentification}_${limit}_${offset}_${validSortBy}_${validSortOrder}`;
      const cached = usersCache.get(cacheKey);

      if (cached) {
        return cached;
      }

      const data = await HttpService.get(
        `/${customer_identification}/${entityIdentification}/entity/users?limit=${limit}&offset=${offset}&sortBy=${validSortBy}&sortOrder=${validSortOrder}`
      ).then((response) => {
        return response?.data?.element;
      });

      usersCache.setWithMediumTTL(cacheKey, data);
      return data;
    },
    {
      onError: (err: AxiosError<any, any>) => {
        toast.error(err.response?.data?.message || err.message);
      },
      ...options,
    }
  );
};

/** `notify: false` for a page that tells the outcome itself, in the language of its visitor. */
export const useUserForgotPassword = ({
  notify = true,
}: { notify?: boolean } = {}) => {
  const { t } = useTranslation();

  return useMutation<any, AxiosError<any, any>, any>(
    ({
      entityIdentification,
      user_email,
    }: {
      entityIdentification: string;
      user_email: string;
    }) =>
      HttpService.get(
        `/user/${
          entityIdentification as string
        }/password?email_address=${encodeURIComponent(user_email)}`
      ),
    {
      onSuccess: () => {
        if (notify) toast.success(t("forgotPassword.msg2"));
      },
      onError: (err) => {
        if (notify) toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

/** `notify: false` for a page that tells the refusal itself, in the language of its visitor. */
export const useUserSocialForgotPassword = ({
  notify = true,
}: { notify?: boolean } = {}) => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      data,
    }: {
      data: {
        providerPayload: { access_token: string; provider: string };
      };
    }) => HttpService.post("/auth/social/reset-link", data),
    {
      onSuccess: () => {},
      onError: (error) => {
        if (notify) toast.error(error.response?.data.message || error.message);
        SecureStorage.clearOAuthCredentials();
      },
    }
  );
};

export const useUserSendMessage = () => {
  const { t } = useTranslation();
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entityIdentification,
      data,
    }: {
      customer_identification: string;
      entityIdentification: string;
      data: any;
    }) =>
      HttpService.post(
        `/${customer_identification}/${entityIdentification}/user/message`,
        data
      ),
    {
      onSuccess: () => {
        toast.success(t("dashboard.message.toast-01"));
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useUserUpdatePassword = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entityIdentification,
      data,
    }: {
      customer_identification: string;
      entityIdentification: string;
      data: any;
    }) =>
      HttpService.patch(
        `/${customer_identification}/${entityIdentification}/user/${customer_identification}/password`,
        data
      ),
    {
      onSuccess: () => {
        toast.success("Password updated");
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useAddUserAutomatically = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entityIdentification,
      data,
    }: {
      customer_identification: string;
      entityIdentification: string;
      data: any;
    }) =>
      HttpService.post(
        `/${customer_identification}/${entityIdentification}/user/add/automatically`,
        data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      ),
    {
      onSuccess: (data, variables) => {
        const countCacheKey = `entity_count_users_${variables.entityIdentification}`;
        const rolesCacheKey = `entity_roles_${variables.entityIdentification}`;
        const typesCacheKey = `entity_types_${variables.entityIdentification}`;
        usersCache.delete(countCacheKey);
        usersCache.delete(rolesCacheKey);
        usersCache.delete(typesCacheKey);

        const statsCacheKeyPattern = `entity_user_statistics_${variables.entityIdentification}`;
        const entityUsersPattern = `entity_users_${variables.entityIdentification}`;
        const userProfilePattern = `user_profile_`;
        usersCache.clearByPattern(statsCacheKeyPattern);
        usersCache.clearByPattern(entityUsersPattern);
        usersCache.clearByPattern(userProfilePattern);

        queryClient.invalidateQueries(["count-users"]);
        queryClient.invalidateQueries(["entity-users"], {
          exact: false,
          refetchActive: true,
        });
        queryClient.invalidateQueries([
          "roles_list",
          variables.customer_identification,
        ]);
        queryClient.invalidateQueries([
          "types_list",
          variables.customer_identification,
        ]);

        toast.success(data.message);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useAddUserManually = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entityIdentification,
      data,
    }: {
      customer_identification: string;
      entityIdentification: string;
      data: any;
    }) =>
      HttpService.post(
        `/${customer_identification}/${entityIdentification}/user/add/manually`,
        data
      ),
    {
      onSuccess: (data, variables) => {
        const countCacheKey = `entity_count_users_${variables.entityIdentification}`;
        const rolesCacheKey = `entity_roles_${variables.entityIdentification}`;
        const typesCacheKey = `entity_types_${variables.entityIdentification}`;
        usersCache.delete(countCacheKey);
        usersCache.delete(rolesCacheKey);
        usersCache.delete(typesCacheKey);

        const statsCacheKeyPattern = `entity_user_statistics_${variables.entityIdentification}`;
        const entityUsersPattern = `entity_users_${variables.entityIdentification}`;
        const userProfilePattern = `user_profile_`;
        usersCache.clearByPattern(statsCacheKeyPattern);
        usersCache.clearByPattern(entityUsersPattern);
        usersCache.clearByPattern(userProfilePattern);

        queryClient.invalidateQueries(["count-users"]);
        queryClient.invalidateQueries(["entity-users"], {
          exact: false,
          refetchActive: true,
        });
        queryClient.invalidateQueries([
          "roles_list",
          variables.customer_identification,
        ]);
        queryClient.invalidateQueries([
          "types_list",
          variables.customer_identification,
        ]);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useUserLogin = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      entity_identification,
      data,
    }: {
      entity_identification: string;
      data: any;
    }) => HttpService.post(`/auth/${entity_identification}/login`, data)
  );
};

export const useUserSocialLogin = () => {
  const navigate = useNavigate();
  const { processId } = useParams();

  let dispatch;
  try {
    const lmcContext = useLmcUserView();
    dispatch = lmcContext.dispatch;
  } catch (error) {
    dispatch = null;
  }

  return useMutation<any, AxiosError<any, any>, any>(
    ({
      entity_identification,
      data,
    }: {
      entity_identification: string;
      data: {
        type: "login";
        providerPayload: any;
        owner_identification?: string;
      };
    }) => HttpService.post(`/auth/${entity_identification}/social/login`, data),
    {
      onSuccess: (data) => {
        if (dispatch) {
          dispatch({
            type: AuthActionKind.LOGIN,
            payload: data.data.element,
          });
        }
      },
      onError: (error) => {
        const code = error.response?.data?.code;

        const isBusinessCard =
          typeof window !== "undefined" &&
          window.location.pathname.startsWith("/business-card/");

        if (code === "RSUSKO026") {
          if (!isBusinessCard) {
            navigate(`/loyalty-program/${processId}/register`, {
              replace: true,
            });
            toast.error(error.response?.data.message || error.message);
          }
          return;
        }

        toast.error(error.response?.data.message || error.message);
      },
    }
  );
};

export const useCustomRegisterSocialLogin = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      entity_identification,
      form_id,
      data,
    }: {
      entity_identification: string;
      form_id?: string;
      data: { type: "login"; providerPayload: any };
    }) => {
      const query = form_id ? `?form_id=${encodeURIComponent(form_id)}` : "";
      return HttpService.post(
        `/auth/${entity_identification}/social/login${query}`,
        data
      );
    }
  );
};

export const useUserRegister = () => {
  let dispatch;
  try {
    const lmcContext = useLmcUserView();
    dispatch = lmcContext.dispatch;
  } catch (error) {
    dispatch = null;
  }
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      entity_identification,
      data,
    }: {
      entity_identification: string;
      data: any;
    }) => HttpService.post(`/auth/${entity_identification}/register`, data),
    {
      onSuccess: (data, variables) => {
        if (dispatch) {
          dispatch({
            type: AuthActionKind.LOGIN,
            payload: {
              ...data.data.element,
              password: variables.data.user_password,
            },
          });
        }
      },
      onError: (error) => {
        toast.error(error.response?.data.message);
      },
    }
  );
};

export const useUserSocialRegister = () => {
  let dispatch;
  try {
    const lmcContext = useLmcUserView();
    dispatch = lmcContext.dispatch;
  } catch (error) {
    dispatch = null;
  }
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      entity_identification,
      data,
    }: {
      entity_identification: string;
      data: { type: "register"; providerPayload: any };
    }) =>
      HttpService.post(`/auth/${entity_identification}/social/register`, data),
    {
      onSuccess: (data) => {
        if (dispatch) {
          dispatch({
            type: AuthActionKind.LOGIN,
            payload: data.data.element,
          });
        }
      },
      onError: (error) => {
        toast.error(error.response?.data.message || error.message);
      },
    }
  );
};

export const useUserResendEmailVerification = () => {
  const { t } = useTranslation("clientView");
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      entity_identification,
      userId,
    }: {
      entity_identification: string;
      userId: string;
    }) =>
      HttpService.post(
        `/link-registration/${entity_identification}/${userId}/resend-validation-email`
      ),
    {
      onSuccess: () => {
        toast.success(t("register.msg"));
      },
      onError: (error) => {
        toast.error(error.response?.data.message);
      },
    }
  );
};

export const useUserRegisterByLink = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      entity_identification,
      data,
    }: {
      entity_identification: string;
      data: any;
    }) =>
      HttpService.post(
        `/link-registration/${entity_identification}/register`,
        data
      )
  );
};

export const useUserRegisterByCustomRegister = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      entity_identification,
      data,
      form_id,
    }: {
      entity_identification: string;
      data: any;
      form_id?: string;
    }) => {
      const query = form_id ? `?form_id=${encodeURIComponent(form_id)}` : "";
      return HttpService.post(
        `/link-registration/${entity_identification}/custom-register${query}`,
        data
      );
    },
    {
      onSuccess: () => {},
    }
  );
};

export const useBusinessCardRegister = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      entity_identification,
      data,
    }: {
      entity_identification: string;
      data: any;
    }) =>
      HttpService.post(`/business-card/${entity_identification}/register`, data)
  );
};

export const useGetCustomRegisterUserProfile = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      entity_identification,
      user_identification,
      token,
    }: {
      entity_identification: string;
      user_identification: string;
      token: string;
    }) =>
      HttpService.get(
        `/link-registration/${entity_identification}/${user_identification}/profile`,
        { headers: { Authorization: token } }
      )
  );
};

export const useUserUpdateByCustomRegister = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      entity_identification,
      user_identification,
      data,
      token,
      form_id,
    }: {
      entity_identification: string;
      user_identification: string;
      data: any;
      token: string;
      form_id?: string;
    }) => {
      const query = form_id ? `?form_id=${encodeURIComponent(form_id)}` : "";
      return HttpService.patch(
        `/link-registration/${entity_identification}/${user_identification}/update${query}`,
        data,
        {
          headers: {
            Authorization: token,
          },
        }
      );
    }
  );
};

export const useGetUserData = (
  customer_identification: string,
  entityIdentification: string,
  customerId: string,
  key: string
) => {
  return useQuery<any, any, any>(
    ["getUserData", customerId],
    async () => {
      return await HttpService.get(
        `/${customer_identification}/${entityIdentification}/user/${customerId}/data/${key}`
      ).then((data) => {
        return data?.data?.element;
      });
    },
    {
      onError: (err: AxiosError<any, any>) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useGetJsonKeyValues = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entityIdentification,
      customerId,
      key,
    }: {
      customer_identification: string;
      entityIdentification: string;
      customerId: string;
      key: string;
    }) =>
      HttpService.get(
        `/${customer_identification}/${entityIdentification}/user/${customerId}/data/${key}`
      ),
    {
      onSuccess: () => {},
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useCreateUserDataWithSpecialKey = () => {
  return useMutation<any, AxiosError<any, any>, UserDataWithSpecialKey>(
    ({
      customer_identification,
      entity_identification,
      customerId,
      key,
      data,
    }: UserDataWithSpecialKey) =>
      HttpService.post(
        `/${customer_identification}/${entity_identification}/user/${customerId}/data/${key}`,
        { user_data: data }
      ),
    {
      onSuccess: () => {},
      onError: (error) => {
        toast.error(error.response?.data.message);
      },
    }
  );
};

export const useUpdateUserDataWithSpecialKey = () => {
  return useMutation<any, AxiosError<any, any>, UserDataWithSpecialKey>(
    ({
      customer_identification,
      entity_identification,
      customerId,
      key,
      data,
    }: UserDataWithSpecialKey) =>
      HttpService.patch(
        `/${customer_identification}/${entity_identification}/user/${customerId}/data/${key}`,
        { user_data: data }
      ),
    {
      onSuccess: (res, variables) => {
        const userDataCacheKey = `user_data_${variables.customerId}`;
        usersCache.delete(userDataCacheKey);
      },
      onError: (error) => {
        toast.error(error.response?.data.message);
      },
    }
  );
};

export const useGetUserDataWithSpecialKey = (
  customer_identification: string,
  entityIdentification: string,
  customerId: string,
  key: string
) => {
  return useQuery<any, any, any>(
    ["getUserDataWithSpecialKey", customerId, key],
    async () => {
      return await HttpService.get(
        `/${customer_identification}/${entityIdentification}/user/${customerId}/data/${key}`
      ).then((data) => {
        return data?.data?.element;
      });
    },
    {
      onError: (err: AxiosError<any, any>) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useGetPhoneValidationStatus = (
  customer_identification: string,
  entityIdentification: string,
  customerId: string
) => {
  return useQuery<any, any, any>(
    ["getPhoneValidationStatus", customerId],
    async () => {
      return await HttpService.get(
        `/${customer_identification}/${entityIdentification}/user/${customerId}/phone-validation`
      ).then((data) => {
        return data?.data?.element;
      });
    },
    {
      onError: (err: AxiosError<any, any>) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useCreateNewKeyMutation = () => {
  return useMutation<
    {
      customer_identification: string;
      entityIdentification: string;
      customerId: string;
      key: string;
      value: string;
    },
    AxiosError<any, any>,
    any
  >(
    ({
      customer_identification,
      entityIdentification,
      customerId,
      key,
      value,
    }: {
      customer_identification: string;
      entityIdentification: string;
      customerId: string;
      key: string;
      value: string;
    }) =>
      HttpService.post(
        `/${customer_identification}/${entityIdentification}/user/${customerId}/data/${key}`,
        { user_data: value }
      ),
    {
      onSuccess: (data, variables) => {
        const user_data_cache = `user_data_${variables.customer_identification}_${variables.entityIdentification}_${variables.customerId}`;
        entityCache.delete(user_data_cache);

        queryClient.invalidateQueries([
          "user-data",
          variables.customer_identification,
          variables.entityIdentification,
          variables.customerId,
        ]);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useUpdateKeyMutation = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entityIdentification,
      customerId,
      key,
      value,
    }: {
      customer_identification: string;
      entityIdentification: string;
      customerId: string;
      key: string;
      value: string;
    }) =>
      HttpService.patch(
        `/${customer_identification}/${entityIdentification}/user/${customerId}/data/${key}`,
        { user_data: value }
      ),
    {
      onSuccess: (data, variables) => {
        const user_data_cache = `user_data_${variables.customer_identification}_${variables.entityIdentification}_${variables.customerId}`;
        entityCache.delete(user_data_cache);

        queryClient.invalidateQueries([
          "user-data",
          variables.customer_identification,
          variables.entityIdentification,
          variables.customerId,
        ]);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useDeleteKeyMutation = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entityIdentification,
      customerId,
      key,
    }: {
      customer_identification: string;
      entityIdentification: string;
      customerId: string;
      key: string;
    }) =>
      HttpService.delete(
        `/${customer_identification}/${entityIdentification}/user/${customerId}/data/${key}`
      ),
    {
      onSuccess: (data, variables) => {
        const user_data_cache = `user_data_${variables.customer_identification}_${variables.entityIdentification}_${variables.customerId}`;
        entityCache.delete(user_data_cache);

        queryClient.invalidateQueries([
          "user-data",
          variables.customer_identification,
          variables.entityIdentification,
          variables.customerId,
        ]);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useRenameDataMutation = () => {
  return useMutation<
    {
      customer_identification: string;
      entityIdentification: string;
      customerId: string;
      old_key: string;
      new_key: string;
      value: string;
    },
    AxiosError<any, any>,
    any
  >(
    ({
      customer_identification,
      entityIdentification,
      customerId,
      old_key,
      new_key,
      value,
    }: {
      customer_identification: string;
      entityIdentification: string;
      customerId: string;
      old_key: string;
      new_key: string;
      value: string;
    }) =>
      HttpService.patch(
        `/${customer_identification}/${entityIdentification}/user/${customerId}/data/${old_key}/rename`,
        {
          user_data: value,
          new_key: new_key,
        }
      ),
    {
      onSuccess: (data, variables) => {
        const user_data_cache = `user_data_${variables.customer_identification}_${variables.entityIdentification}_${variables.customerId}`;
        entityCache.delete(user_data_cache);

        queryClient.invalidateQueries([
          "user-data",
          variables.customer_identification,
          variables.entityIdentification,
          variables.customerId,
        ]);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useGetProfileDataKeyValues = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entityIdentification,
      customerId,
      key,
    }: {
      customer_identification: string;
      entityIdentification: string;
      customerId: string;
      key: string;
    }) =>
      HttpService.get(
        `/${customer_identification}/${entityIdentification}/user/${customerId}/profile-data/${key}`
      ),
    {
      onSuccess: () => {},
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useCreateProfileDataKey = () => {
  return useMutation<
    {
      customer_identification: string;
      entityIdentification: string;
      customerId: string;
      key: string;
      value: string;
    },
    AxiosError<any, any>,
    any
  >(
    ({
      customer_identification,
      entityIdentification,
      customerId,
      key,
      value,
    }: {
      customer_identification: string;
      entityIdentification: string;
      customerId: string;
      key: string;
      value: string;
    }) =>
      HttpService.post(
        `/${customer_identification}/${entityIdentification}/user/${customerId}/profile-data/${key}`,
        { user_data: value }
      ),
    {
      onSuccess: (data, variables) => {
        const user_data_cache = `user_profile_data_${variables.customer_identification}_${variables.entityIdentification}_${variables.customerId}`;
        entityCache.delete(user_data_cache);

        queryClient.invalidateQueries([
          "user-profile-data",
          variables.customer_identification,
          variables.entityIdentification,
          variables.customerId,
        ]);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useUpdateProfileDataKey = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entityIdentification,
      customerId,
      key,
      value,
    }: {
      customer_identification: string;
      entityIdentification: string;
      customerId: string;
      key: string;
      value: string;
    }) =>
      HttpService.patch(
        `/${customer_identification}/${entityIdentification}/user/${customerId}/profile-data/${key}`,
        { user_data: value }
      ),
    {
      onSuccess: (data, variables) => {
        const user_data_cache = `user_profile_data_${variables.customer_identification}_${variables.entityIdentification}_${variables.customerId}`;
        entityCache.delete(user_data_cache);

        queryClient.invalidateQueries([
          "user-profile-data",
          variables.customer_identification,
          variables.entityIdentification,
          variables.customerId,
        ]);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useDeleteProfileDataKey = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entityIdentification,
      customerId,
      key,
    }: {
      customer_identification: string;
      entityIdentification: string;
      customerId: string;
      key: string;
    }) =>
      HttpService.delete(
        `/${customer_identification}/${entityIdentification}/user/${customerId}/profile-data/${key}`
      ),
    {
      onSuccess: (data, variables) => {
        const user_data_cache = `user_profile_data_${variables.customer_identification}_${variables.entityIdentification}_${variables.customerId}`;
        entityCache.delete(user_data_cache);

        queryClient.invalidateQueries([
          "user-profile-data",
          variables.customer_identification,
          variables.entityIdentification,
          variables.customerId,
        ]);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useRenameProfileData = () => {
  return useMutation<
    {
      customer_identification: string;
      entityIdentification: string;
      customerId: string;
      old_key: string;
      new_key: string;
      value: string;
    },
    AxiosError<any, any>,
    any
  >(
    ({
      customer_identification,
      entityIdentification,
      customerId,
      old_key,
      new_key,
      value,
    }: {
      customer_identification: string;
      entityIdentification: string;
      customerId: string;
      old_key: string;
      new_key: string;
      value: string;
    }) =>
      HttpService.patch(
        `/${customer_identification}/${entityIdentification}/user/${customerId}/profile-data/${old_key}/rename`,
        {
          user_data: value,
          new_key: new_key,
        }
      ),
    {
      onSuccess: (data, variables) => {
        const user_data_cache = `user_profile_data_${variables.customer_identification}_${variables.entityIdentification}_${variables.customerId}`;
        entityCache.delete(user_data_cache);

        queryClient.invalidateQueries([
          "user-profile-data",
          variables.customer_identification,
          variables.entityIdentification,
          variables.customerId,
        ]);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useUpdateUserEmail = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      entity_identification,
      user_identification,
      data,
      authorization,
    }: UpdateUserProps) =>
      HttpService.patch(
        `/entity/${entity_identification}/lmc/email/${user_identification}`,
        data,
        {
          auth: authorization,
        }
      ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["lmc-user-profile"]);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useGetBusinessCardProcessId = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
      user_identification,
    }: {
      customer_identification: string;
      entity_identification: string;
      user_identification: string;
    }) =>
      HttpService.get(
        `/${customer_identification}/${entity_identification}/user/${user_identification}/business-card/process-id`
      ),
    {
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useAssignBusinessCard = () => {
  return useMutation<
    any,
    AxiosError<any, any>,
    {
      customer_identification: string;
      entity_identification: string;
      user_identification: string;
      job_title: string;
      description?: string;
      links?: unknown[];
      uploaded_files?: unknown[];
      booking: { enabled: boolean; type_id: string | null };
    }
  >(
    ({
      customer_identification,
      entity_identification,
      user_identification,
      job_title,
      description,
      links,
      uploaded_files,
      booking,
    }) =>
      HttpService.post(
        `/${customer_identification}/${entity_identification}/user/${user_identification}/business-card/assign`,
        {
          job_title,
          description,
          links,
          uploaded_files,
          booking,
        }
      ),
    {
      onSuccess: (_res, variables) => {
        usersCache.delete(`user_profile_${variables.user_identification}`);

        const cacheBase = `entity_preferences_${variables.customer_identification}_${variables.entity_identification}`;
        entityCache.delete(cacheBase);
        entityCache.delete(`${cacheBase}_business_card_form`);

        groupsCache.clearByPattern(`groups_${variables.entity_identification}`);
        groupsCache.delete(
          `entity_count_users_${variables.entity_identification}`
        );

        queryClient.invalidateQueries([
          "entityPreferencesByPath",
          variables.customer_identification,
          variables.entity_identification,
          "business_card_form",
        ]);

        queryClient.invalidateQueries([
          "entity_user",
          variables.user_identification,
        ]);
        queryClient.invalidateQueries([queryKeys.group_users]);
        queryClient.invalidateQueries([queryKeys.groups]);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useSaveBusinessCardStatistics = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      entity_identification,
      user_identification,
      event_key,
      data,
    }: SaveStatisticsBusinessCard) =>
      HttpService.post(
        `/entity/${entity_identification}/${user_identification}/event/${event_key}`,
        data
      ),
    {
      onSuccess: (res, variables) => {},
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useGetCustomRegisterProcessId = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
      user_identification,
      form_id,
    }: {
      customer_identification: string;
      entity_identification: string;
      user_identification: string;
      form_id?: string;
    }) => {
      const query = form_id ? `?form_id=${encodeURIComponent(form_id)}` : "";
      return HttpService.get(
        `/${customer_identification}/${entity_identification}/user/${user_identification}/custom-registration/process-id${query}`
      );
    },
    {
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useVerifyEmail = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      entity_identification,
      user_identification,
    }: {
      entity_identification: string;
      user_identification: string;
    }) =>
      HttpService.get(
        `/user/${entity_identification}/${user_identification}/email-status`
      )
  );
};

export const useGetUserPaymentHistory = (
  customer_identification: string,
  entity_identification: string,
  user_identification: string,
  enabled: boolean = true
) => {
  return useQuery<any, any, any>(
    [
      "user-payment-history-grouped",
      customer_identification,
      entity_identification,
      user_identification,
    ],
    async () => {
      const cacheKey = `user_payment_history_grouped_${user_identification}`;
      const cached = usersCache.get(cacheKey);

      if (cached) {
        return cached;
      }

      const data = await HttpService.get(
        `/${customer_identification}/${entity_identification}/paymod/user-payment-history-grouped/${user_identification}`
      ).then((response) => {
        const raw = response?.data?.element || [];
        return raw.map((group: any) => ({
          planCode: group.plan_code || "",
          planDescription: group.plan_description || "",
          payment_type: group.payment_type,
          paymentSteps: group.payment_steps || [],
        }));
      });

      usersCache.setWithMediumTTL(cacheKey, data);
      return data;
    },
    {
      enabled,
      onError: (err: AxiosError<any, any>) => {
        console.error(
          "Error fetching payment history:",
          err.response?.data?.message || err.message
        );
      },
    }
  );
};

export const useStartPhoneValidation = () => {
  const { t } = useTranslation();

  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entityIdentification,
      user_identification,
    }: GetEntityUSerProps) =>
      HttpService.post(
        `/${customer_identification}/${entityIdentification}/user/${user_identification}/phone-validation`,
        null
      ),
    {
      onSuccess: () => {
        toast.success(t("phoneValidation.codeSent"));
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useConfirmPhoneValidation = () => {
  const { t } = useTranslation();

  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entityIdentification,
      user_identification,
      code,
    }: GetEntityUSerProps & { code: string }) =>
      HttpService.post(
        `/${customer_identification}/${entityIdentification}/user/${user_identification}/phone-validation/confirm`,
        { code }
      ),
    {
      onSuccess: () => {
        toast.success(t("phoneValidation.phoneValidated"));
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useStartPhoneValidationForCustomRegistration = () => {
  const { t } = useTranslation("clientView");

  return useMutation<any, AxiosError<any, any>, any>(
    ({
      entity_identification,
      user_identification,
      token,
    }: {
      entity_identification: string;
      user_identification: string;
      token: string;
    }) =>
      HttpService.post(
        `/link-registration/${entity_identification}/${user_identification}/phone-validation`,
        null,
        {
          headers: {
            Authorization: token,
          },
        }
      ),
    {
      onSuccess: () => {
        toast.success(t("phoneValidation.verificationCodeSent"));
      },
    }
  );
};

export const useConfirmPhoneValidationForCustomRegistration = () => {
  const { t } = useTranslation("clientView");

  return useMutation<any, AxiosError<any, any>, any>(
    ({
      entity_identification,
      user_identification,
      code,
      token,
    }: {
      entity_identification: string;
      user_identification: string;
      code: string;
      token: string;
    }) =>
      HttpService.post(
        `/link-registration/${entity_identification}/${user_identification}/phone-validation/confirm`,
        { code },
        {
          headers: {
            Authorization: token,
          },
        }
      ),
    {
      onSuccess: () => {
        toast.success(t("phoneValidation.phoneValidated"));
      },
    }
  );
};
